import { useEffect, type ReactNode } from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { z } from 'zod'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2 } from 'lucide-react'
import { useReferenceIndex } from '@/features/service/hooks'
import { useCustomers } from '@/features/customers/hooks'
import { customerOptionLabel } from '@/features/customers/customer-utils'
import { PriceInput } from '@/components/ui/price-input'
import { BOAT_TYPES, BOAT_TYPE_LABELS } from '@/types/inventory'

// Engine manufacturer keys — mirrors internal/shared/enginedata/data.json.
// The reference index supplies the labels; the fallback keeps the form
// usable before it loads. Shared with the inspection intake dialog.
export const ENGINE_MANUFACTURERS = [
  { value: 'mercury', label: 'Mercury / MerCruiser' },
  { value: 'yamaha', label: 'Yamaha' },
  { value: 'honda', label: 'Honda' },
  { value: 'suzuki', label: 'Suzuki' },
  { value: 'volvo_penta', label: 'Volvo Penta' },
  { value: 'evinrude', label: 'Evinrude (E-TEC)' },
  { value: 'cmd', label: 'Cummins MerCruiser Diesel' },
  { value: 'indmar', label: 'Indmar' },
  { value: 'pcm', label: 'PCM' },
  { value: 'crusader', label: 'Crusader' },
  { value: 'yanmar', label: 'Yanmar' },
  { value: 'boat', label: 'Other / boat-level only' },
]

export const boatFormSchema = z.object({
  make: z.string().min(1, 'Make is required'),
  model: z.string().min(1, 'Model is required'),
  year: z.coerce
    .number()
    .int()
    .min(1900, 'Year must be 1900 or later')
    .max(2100, 'Year looks too far in the future'),
  price: z.coerce.number().gt(0, 'Price must be greater than 0'),
  cost: z.coerce.number().gte(0, 'Cost cannot be negative'),
  length_ft: z.coerce.number().gt(0, 'Length must be greater than 0'),
  status: z.enum(['available', 'sold', 'in_service', 'in_storage']),
  listing_state: z.enum(['for_sale', 'sold', 'off_market']),
  condition: z.enum(['new', 'used']),
  type: z.enum(BOAT_TYPES),
  color: z.string().max(100, 'Color is at most 100 characters').optional(),
  description: z.string().max(5000, 'Description is at most 5000 characters').optional(),
  engine_manufacturer: z.string().optional(),
  engine_model: z.string().optional(),
  engine_count: z.coerce
    .number()
    .int('Engine count must be a whole number')
    .gte(0, 'Engine count cannot be negative')
    .lte(20, 'Engine count looks too high')
    .optional(),
  engine_serial: z.string().max(100, 'Engine serial is at most 100 characters').optional(),
  engine_hours: z
    .union([z.coerce.number().gte(0, 'Hours cannot be negative'), z.literal('')])
    .optional(),
  hin: z.string().max(20, 'HIN is at most 20 characters').optional(),
  customer_id: z.string().optional(),
})

export type BoatFormValues = z.infer<typeof boatFormSchema>

const fieldSchemas = boatFormSchema.shape

interface BoatFormProps {
  /** Reports dirty state up so the host dialog can warn before closing. */
  onDirtyChange?: (dirty: boolean) => void
  defaultValues?: Partial<BoatFormValues>
  onSubmit: (values: BoatFormValues) => void
  isPending?: boolean
  submitLabel?: string
  /** Extra content (e.g. a photo picker) rendered above the submit button. */
  children?: ReactNode
}

export function BoatForm({
  onDirtyChange,
  defaultValues,
  onSubmit,
  isPending,
  submitLabel = 'Save',
  children,
}: BoatFormProps) {
  const { data: reference } = useReferenceIndex()
  const { data: customers } = useCustomers()
  const manufacturerOptions =
    reference?.manufacturers
      .filter((m) => m.key !== 'boat')
      .map((m) => ({ value: m.key, label: m.name })) ?? ENGINE_MANUFACTURERS

  const initialValues: BoatFormValues = {
    make: '',
    model: '',
    year: new Date().getFullYear(),
    price: 0,
    cost: 0,
    length_ft: 0,
    status: 'available',
    listing_state: 'for_sale',
    condition: 'used',
    type: 'other',
    color: '',
    description: '',
    engine_manufacturer: '',
    engine_model: '',
    engine_count: 1,
    engine_serial: '',
    engine_hours: '',
    hin: '',
    customer_id: '',
    ...defaultValues,
  }

  const form = useForm({
    defaultValues: initialValues,
    onSubmit: ({ value }) => {
      onSubmit({
        ...value,
        year: Number(value.year),
        price: Number(value.price),
        cost: Number(value.cost),
        length_ft: Number(value.length_ft),
        engine_count: Number(value.engine_count ?? 1),
        engine_hours:
          value.engine_hours === '' || value.engine_hours === undefined
            ? ''
            : Number(value.engine_hours),
        engine_manufacturer: value.engine_manufacturer === 'none' ? '' : value.engine_manufacturer,
      })
    },
  })

  const isDirty = useStore(form.store, (state) => state.isDirty)
  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  return (
    <Form form={form}>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void form.handleSubmit()
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-2 gap-4">
          <FormField
            name="make"
            validators={{ onChange: fieldSchemas.make }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Make</FormLabel>
                <FormControl>
                  <Input placeholder="Yamaha" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="model"
            validators={{ onChange: fieldSchemas.model }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Model</FormLabel>
                <FormControl>
                  <Input placeholder="242X" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <FormField
            name="year"
            validators={{ onChange: fieldSchemas.year }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Year</FormLabel>
                <FormControl>
                  <Input type="number" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="price"
            validators={{ onChange: fieldSchemas.price }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Price ($)</FormLabel>
                <FormControl>
                  <PriceInput
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder="24,999"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="cost"
            validators={{ onChange: fieldSchemas.cost }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cost ($)</FormLabel>
                <FormControl>
                  <PriceInput
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder="18,500"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="length_ft"
            validators={{ onChange: fieldSchemas.length_ft }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Length (ft)</FormLabel>
                <FormControl>
                  <Input type="number" step="0.1" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="color"
            validators={{ onChange: fieldSchemas.color }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Color</FormLabel>
                <FormControl>
                  <Input placeholder="Midnight Blue" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <FormField
            name="type"
            validators={{ onChange: fieldSchemas.type }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Boat Type</FormLabel>
                <Select onValueChange={field.onChange} value={field.value as string}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {BOAT_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {BOAT_TYPE_LABELS[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
                <p className="text-xs text-muted-foreground">
                  Drives which add-ons fit this boat on a deal.
                </p>
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          <FormField
            name="status"
            validators={{ onChange: fieldSchemas.status }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Operational Status</FormLabel>
                <Select onValueChange={field.onChange} value={field.value as string}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="in_service">In Service</SelectItem>
                    <SelectItem value="in_storage">In Storage</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="listing_state"
            validators={{ onChange: fieldSchemas.listing_state }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Listing State</FormLabel>
                <Select onValueChange={field.onChange} value={field.value as string}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Listing" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="for_sale">For Sale</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                    <SelectItem value="off_market">Off Market</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="condition"
            validators={{ onChange: fieldSchemas.condition }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Condition</FormLabel>
                <Select onValueChange={field.onChange} value={field.value as string}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Condition" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="used">Used</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <FormField
            name="engine_manufacturer"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Engine Manufacturer</FormLabel>
                <Select onValueChange={field.onChange} value={(field.value as string) || 'none'}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Unknown" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">Unknown / none</SelectItem>
                    {manufacturerOptions.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="engine_model"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Engine Model</FormLabel>
                <FormControl>
                  <Input placeholder="Raptor 400" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <FormField
            name="engine_count"
            validators={{ onChange: fieldSchemas.engine_count }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Engine Count</FormLabel>
                <FormControl>
                  <Input type="number" min="0" max="20" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="engine_serial"
            validators={{ onChange: fieldSchemas.engine_serial }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Engine Serial</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. 6X6K-123456" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="engine_hours"
            validators={{ onChange: fieldSchemas.engine_hours }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Engine Hours</FormLabel>
                <FormControl>
                  <Input type="number" step="0.1" placeholder="450" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="hin"
            validators={{ onChange: fieldSchemas.hin }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>HIN</FormLabel>
                <FormControl>
                  <Input placeholder="Hull ID (optional)" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          name="customer_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Customer (optional)</FormLabel>
              <Select
                onValueChange={(v) => field.onChange(v === 'none' ? '' : v)}
                value={(field.value as string) || 'none'}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="none">No customer</SelectItem>
                  {(customers ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {customerOptionLabel(c)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name="description"
          validators={{ onChange: fieldSchemas.description }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Listing description, features, trailer, service history…"
                  className="h-28 resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {children}
        <Button type="submit" disabled={isPending} className="w-full">
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitLabel}
        </Button>
      </form>
    </Form>
  )
}
